<template>
    <!-- Start no results message -->
    <div>
        <l-surveys-insights-filters
            :survey="survey"
            :allowed-filters="['period', 'question', 'category']"
            :show-sort-by="true"
            :show-group-by1="true"
            :allowed-group-by1s="
                [
                    {
                        value: 'question',
                        text: $t('surveys.details.labelAllQuestions'),
                    },
                    {
                        value: 'category',
                        text: $t('surveys.details.labelAllTopics'),
                    },
                    hasMixedScales
                        ? null
                        : {
                              value: '',
                              text: $t('surveys.details.labelTotalScore'),
                          },
                ].filter(Boolean)
            "
            @change="onFilterChange"
        ></l-surveys-insights-filters>

        <div v-show="list.adjustedFilters">
            <hr />
            <i class="fal fa-info"></i>
            <span>{{ $t("surveys.details.labelAdjustedFilter") }} </span>
        </div>

        <hr style="margin-bottom: 10px" />

        <div
            v-show="!list.results.length && survey.periods.length"
            class="text-center"
            data-test-id="survey-details-empty"
        >
            <br />
            <img alt="" :src="noDataIcon" class="placeholder-list" />
            <h4 class="color-grey">
                <span>{{ $t("surveys.details.noDataH1") }} </span>
            </h4>
        </div>

        <div v-show="!list.results.length && !survey.periods.length" class="text-center">
            <br />
            <img alt="" :src="icon" height="80" />
            <h4 class="color-grey">
                <span>{{ $t("surveys.details.labelNextRoundStarts") }} </span>
                {{ survey.timing.nextPeriodStart | moment("MMM D") }}
            </h4>
            <p class="color-grey">
                <span>{{ $t("surveys.details.labelNextRoundDelay") }} </span>
            </p>
        </div>
        <!-- End no results message -->

        <!-- hide it when its detail page -->
        <div v-for="(listItem, index) in listResult" v-show="view === 'LIST'" :key="index">
            <l-surveys-insights-list-item
                :list-item="listItem"
                :group-by="groupBy"
                :is-owner="survey.isOwner"
                :can-see-benchmark="permissionCheck('BENCHMARK_EXTERNAL')"
                @showDetail="showDetail(listItem)"
            />

            <div class="hr-small"></div>
        </div>

        <!-- item details if category is not null, then we are looking at the detail of the question/category -->
        <div v-if="view == 'DETAIL'">
            <div class="ellipse-text">
                <h4 class="color-dark">
                    <strong>
                        {{ detail[groupBy] && detail[groupBy].name ? detail[groupBy].name : "All" }}
                    </strong>
                </h4>
                <span
                    tabindex="0"
                    class="color-primary clickable"
                    @click="showList()"
                    @keyup.enter="showList()"
                >
                    <span>{{ $t("surveys.details.menuBack") }} </span>
                </span>
            </div>

            <br />

            <div class="row">
                <div class="col-md-3">
                    <span class="survey-score-large">
                        {{ detail.avg_amt }}
                    </span>
                    <span> / {{ detail.scaleType === "nps" || !detail.scaleType ? 10 : 5 }} </span>
                    <span v-show="detail.change > 0" class="explanation color-success">
                        <i style="margin: 0 10px;" class="fal fa-chart-line"></i>
                        <span>
                            {{
                                $t("surveys.details.labelScoreUp", {
                                    change: detail.change,
                                })
                            }}
                        </span>
                    </span>
                    <span v-show="detail.change < 0" class="explanation color-warning">
                        <i style="margin: 0 10px;" class="fal fa-chart-line-down"></i>
                        <span>
                            {{
                                $t("surveys.details.labelScoreDown", {
                                    change: detail.change * -1,
                                })
                            }}
                        </span>
                    </span>
                    <span
                        v-show="
                            permissionCheck('BENCHMARK_EXTERNAL') &&
                                (detail.scaleType === 'nps' || !detail.scaleType)
                        "
                    >
                        <div class="hr-small"></div>

                        <i class="fal fa-chart-bar" style="padding-right: 7px;"></i>
                        <span
                            v-if="
                                detail[groupBy] &&
                                    detail[groupBy].surveyBenchmark &&
                                    detail[groupBy].surveyBenchmark.avg
                            "
                            v-b-popover.hover="{
                                content: $t('surveys.details.labelBenchmarkHelp'),
                            }"
                            class="color-grey"
                        >
                            <span>{{ $t("surveys.details.labelBenchmark") }}</span
                            >:
                            {{ detail[groupBy].surveyBenchmark.avg }}
                        </span>
                        <span v-else class="explanation color-grey" style="padding-right: 7px;">
                            <span>{{ $t("surveys.details.labelNoBenchmark") }} </span>
                        </span>
                    </span>

                    <span
                        v-if="survey.isOwner && detail.question && detail.question.impact != null"
                    >
                        <div class="hr-small"></div>

                        <i class="fal fa-adjust" style="padding-right: 7px;"></i>

                        <span class="color-grey">
                            <span
                                v-b-popover.hover="{
                                    content: $t('surveys.details.helpImpact'),
                                }"
                                >{{ $t("surveys.details.labelImpact") }}</span
                            >:
                            <span
                                v-b-popover.hover="{
                                    content: $t(
                                        `surveys.details.helpImpactLevel.${detail.question.impact}`,
                                    ),
                                }"
                            >
                                {{
                                    $t(`surveys.details.labelImpactLevel.${detail.question.impact}`)
                                }}
                            </span>
                        </span>
                    </span>

                    <div class="hr-small"></div>

                    <i class="fal fa-calendar" style="padding-right: 7px;"></i>

                    <span
                        v-b-popover.hover="{
                            content: $t('surveys.details.helpDate'),
                        }"
                        class="color-grey"
                    >
                        {{ detail.periodStart | moment("MMM D, YYYY") }}
                    </span>

                    <div class="hr-small"></div>

                    <i class="fal fa-users" style="padding-right: 7px;"></i>

                    <span class="color-grey">
                        {{ detail.cnt_distinct_sender }}
                        <span>{{ $t("surveys.details.labelParticipants") }} </span>
                    </span>

                    <div class="hr-small"></div>

                    <i
                        v-if="detail.scaleType === 'nps' || !detail.scaleType"
                        class="fal fa-thumbs-up"
                        style="padding-right: 7px;"
                    ></i>

                    <span
                        v-if="detail.scaleType === 'nps' || !detail.scaleType"
                        v-b-popover.hover="{
                            content: $t('surveys.details.helpNPS'),
                        }"
                        class="color-grey"
                    >
                        {{
                            (detail.percentage_positive - detail.percentage_negative).toFixed(0)
                        }}&#37;
                    </span>
                </div>
                <div class="col-md-9">
                    <l-chart
                        type="line"
                        height="150"
                        :colors="['primary', 'grey', 'green']"
                        :data="detail.data"
                        :options="chartOptions"
                    />
                    <p class="explanation text-center">
                        <i class="fal fa-eye r-default-margin-s"></i>
                        <span>{{ $t("surveys.details.helpResultValidity") }} </span>
                    </p>
                </div>
            </div>

            <div v-show="detail.questions && detail.questions.length">
                <hr />
                <h4 class="color-dark">
                    <strong
                        ><span>{{ $t("surveys.details.labelInfluencerQuestions") }} </span></strong
                    >
                </h4>
            </div>

            <!-- influencer questions -->
            <div v-for="(listItem, index) in detail.questions" :key="index">
                <l-surveys-insights-list-item
                    :list-item="listItem"
                    :group-by="'question'"
                    :is-owner="survey.isOwner"
                    :can-see-benchmark="permissionCheck('BENCHMARK_EXTERNAL')"
                    @showDetail="() => {}"
                />
                <div class="hr-small"></div>
            </div>

            <!-- end influencer / contributing questions -->
        </div>

        <!-- end item details -->
    </div>

    <!-- End Multiple Choice Results -->
</template>

<script>
import { mapGetters } from "vuex";

import LChart from "@/components/LChart";

import icon from "@/img/icons/surveys.png";
import noDataIcon from "@/img/icons/nodata.png";

import LSurveysInsightsFilters from "@/components/LSurveysInsightsFilters.vue";
import LSurveysInsightsListItem from "@/components/LSurveysInsightsListItem.vue";

export default {
    components: {
        LChart,
        LSurveysInsightsFilters,
        LSurveysInsightsListItem,
    },
    props: {
        survey: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            sortBy: (a, b) => b.avg_amt - a.avg_amt || b.name?.localeCompare(a.name),
            filterIds: {},

            groupBy: "", // question or category or total
            showModal: false, // modal for action-items.
            filters: {},
            actionItem: {},
            isCopy: false,
            newComment: "",
            expandSuggestion: false,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",

            questionList: "surveys/surveys/questionListResult",
            categoryList: "surveys/surveys/categoryListResult",

            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",

            teamSettings: "users/current/teamSettings",
            currentUser: "users/current/user",
        }),
        companyName() {
            return this.teamSettings.name;
        },
        list() {
            if (this.groupBy === "question") {
                return this.questionList;
            }
            return this.categoryList;
        },

        listResult() {
            // bad data structure so I have to nest this
            const sortBy = this.sortBy;

            if (this.groupBy === "question") {
                // FIXME not a good solution
                return [...this.questionList.results].sort(sortBy);
            }
            return [...this.categoryList.results].sort(sortBy);
        },
        view() {
            if (this.$route.params.category) {
                return "DETAIL";
            } else {
                return "LIST";
            }
        },
        detail() {
            // const item = {};
            const id = this.$route.params.category;
            const item = this.listResult?.find(x => x._id === id);
            if (!item) {
                return { [this.groupBy]: {} };
            }
            const detail = {
                ...item,
                data: {
                    labels: item.labels,
                    datasets: [
                        {
                            data: item.data[0],
                            label: this.$t("surveys.details.labelResultOverall"),
                            yAxisID: "score",
                            fill: true,
                            lineTension: 0,
                        },
                        {
                            data: item.data[1],
                            label: this.$t("surveys.details.labelResultAverage"),
                            yAxisID: "score",
                            fill: false,
                            lineTension: 0,
                        },
                        ...(item.scaleType !== "nps" && item.scaleType // 5 point scale has no NPS.
                            ? []
                            : [
                                  {
                                      data: item.data[2],
                                      label: "NPS",
                                      yAxisID: "nps",
                                      fill: false,
                                      lineTension: 0,
                                  },
                              ]),
                    ],
                },
            };
            if (this.groupBy === "category") {
                if (!this.questionList?.results?.length) {
                    // if we don't have question, call the API to get quesetion results.
                    // when user enter the detail page directly
                    this.$store.dispatch("surveys/surveys/getListResult", {
                        payload: {
                            survey: this.$route.params.id,
                            groupBy1: "question",
                            groupBy2: "period",
                            filterBy: this.filterIds,
                            format: "json",
                        },
                    });
                }
                detail.questions = this.questionList?.results?.filter(q => {
                    return item.category?._id === q.question?.category;
                });
            }
            return detail;
        },

        hasMixedScales() {
            const relevantQuestions = this.survey.questions.filter(question =>
                ["structured", "structuredOnly"].includes(question.skill?.answerType),
            );

            // contains both, 10 and 5-point scale
            return (
                relevantQuestions.some(question => question.scaleType !== "nps") &&
                relevantQuestions.some(question => question.scaleType === "nps")
            );
        },

        chartOptions() {
            return {
                legend: {
                    display: true,
                    position: "bottom",
                    labels: {
                        boxWidth: 20,
                    },
                },
                animation: false,
                scales: {
                    yAxes: [
                        {
                            id: "score",
                            type: "linear",
                            display: true,
                            position: "left",
                            ticks: {
                                suggestedMax:
                                    this.detail?.scaleType === "nps" || !this.detail?.scaleType
                                        ? 10
                                        : 5,
                                suggestedMin: 0,
                                maxTicksLimit:
                                    this.detail?.scaleType === "nps" || !this.detail?.scaleType
                                        ? 3
                                        : 4,
                            },
                            scaleLabel: {
                                display: false,
                                labelString: "10 = Best",
                            },
                        },
                        ...(this.detail?.scaleType !== "nps" && this.detail?.scaleType
                            ? []
                            : [
                                  {
                                      id: "nps",
                                      type: "linear",
                                      position: "right",
                                      ticks: {
                                          min: -100,
                                          max: 100,
                                          stepSize: 50,
                                      },
                                      gridLines: { display: false },
                                  },
                              ]),
                    ],
                    xAxes: [
                        {
                            offset: true,
                            type: "time",
                            time: {
                                unit: "week",
                                tooltipFormat: "ll",
                                unitStepSize: 1,
                            },
                            ticks: {
                                autoSkip: true,
                                maxTicksLimit: 20,
                                fontColor: "#c2c2c2",
                                maxRotation: 45,
                                minRotation: 45,
                            },
                            gridLines: {
                                display: false,
                            },
                        },
                    ],
                    gridLines: {
                        zeroLineWidth: 5,
                    },
                },
                tooltips: {
                    enabled: true,
                    intersect: false,
                    mode: "label",
                },
                hover: {
                    mode: "nearest",
                    intersect: true,
                },
            };
        },
    },

    watch: {
        questionList: {
            immediate: true,
            handler(questionList) {
                this.$emit("change", questionList?.minObsAnonymity);
            },
        },
        categoryList: {
            immediate: true,
            handler(categoryList) {
                this.$emit("change", categoryList?.minObsAnonymity);
            },
        },
    },
    created() {
        this.icon = icon;
        this.noDataIcon = noDataIcon;
    },

    methods: {
        showList() {
            const { query, params } = this.$route;
            this.$router.push({ params: { ...params, category: undefined }, query });
        },
        showDetail(item) {
            const { query, params } = this.$route;
            this.$router.push({ params: { ...params, category: item._id }, query });
        },

        onFilterChange(type, { sortBy, groupBy1, groupBy2, filters }) {
            this.groupBy = groupBy1;
            this.filters = filters;

            if (type !== "init") {
                const { params } = this.$route;
                if (params.category) {
                    // if we are in detail page and user change groupBy or sorting
                    this.$router.push({
                        params: { ...params, category: undefined },
                        query: { sortBy, groupBy1, groupBy2 },
                    });
                }
            }

            if (type === "sortBy") {
                if (sortBy === "-question.impact") {
                    this.sortBy = (a, b) =>
                        b.question?.impact - a.question?.impact || b.name?.localeCompare(a.name);
                } else {
                    this.sortBy = (a, b) => b.avg_amt - a.avg_amt || b.name?.localeCompare(a.name);
                }
                this.$forceUpdate();
                return;
            }

            if (groupBy1 === "category") {
                this.$store.dispatch("surveys/surveys/getListResult", {
                    payload: {
                        survey: this.$route.params.id,
                        groupBy1: "category",
                        groupBy2: "period",
                        filterBy: filters,
                        format: "json",
                    },
                });
            } else if (groupBy1 === "question") {
                this.$store.dispatch("surveys/surveys/getListResult", {
                    payload: {
                        survey: this.$route.params.id,
                        groupBy1: "question",
                        groupBy2: "period",
                        filterBy: filters,
                        format: "json",
                    },
                });
            } else if (groupBy1 === "") {
                // total score, just remove groupBy1
                this.$store.dispatch("surveys/surveys/getListResult", {
                    payload: {
                        survey: this.$route.params.id,
                        groupBy1: "",
                        groupBy2: "period",
                        filterBy: filters,
                        format: "json",
                    },
                });
            }
        },
        permissionCheck(module) {
            if (this.rights.S_ACCESS_ALL) {
                return true;
            }
            return this.survey.sharedWithMe?.scope?.includes(module);
        },
        export() {
            let filterBy = { ...this.filters };
            filterBy.emptyContent = 1; // force export empty content data.
            filterBy.excludeComments = true; // exclude comments from export
            this.$store.dispatch("surveys/surveys/export", {
                payload: {
                    survey: this.$route.params.id,
                    filterBy,
                    format: "xlsx",
                },
            });
        },
        goTo(url) {
            this.$router.push(url);
        },
    },
};
</script>

<style scoped></style>
